import { useRef } from "react";
import { ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { tx, useLang } from "../../../lib/i18n";
import { AdminLabel } from "../../../pages/admin/AdminPages";
import { loadImage } from "./ops";

const TYPES = [["transparent", "Transparent"], ["solid", "Solid colour"], ["gradient", "Gradient"], ["white", "White studio"], ["black", "Black luxury"], ["turquoise", "MIVO turquoise"], ["image", "Custom image"]];

export function BackgroundPanel({ bg, onChange, bgImage, setBgImage }) { useLang();
  const fileRef = useRef();
  const set = (patch) => onChange({ ...bg, ...patch });
  const onFile = async (e) => {
    const f = e.target.files[0]; e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type) || f.size > 15 * 1024 * 1024) { toast.error(tx("Use a PNG, JPG or WebP image under 15MB")); return; }
    try { setBgImage(await loadImage(URL.createObjectURL(f))); set({ type: "image" }); } catch (err) { toast.error(tx(err.message)); }
  };
  return (
    <div className="space-y-3 rounded-xl border border-[var(--mv-border)] p-3" data-testid="studio-background">
      <AdminLabel>{tx("Background (free, no AI)")}</AdminLabel>
      <div className="flex flex-wrap gap-1.5">
        {TYPES.map(([k, l]) => (
          <button key={k} onClick={() => (k === "image" && !bgImage ? fileRef.current.click() : set({ type: k }))}
            className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold ${bg.type === k ? "border-[var(--mv-teal)] bg-[var(--mv-teal-dim)] text-[var(--mv-teal)]" : "border-[var(--mv-border-2)]"}`} data-testid={`studio-bg-${k}`}>{tx(l)}</button>
        ))}
      </div>
      {(bg.type === "solid" || bg.type === "gradient") && (
        <div className="flex flex-wrap items-center gap-3">
          <input type="color" value={bg.color} onChange={(e) => set({ color: e.target.value })} className="h-9 w-12 rounded border border-[var(--mv-border-2)] bg-transparent" data-testid="studio-bg-color" />
          <input value={bg.color} onChange={(e) => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && set({ color: e.target.value })} className="w-24 rounded-lg border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-2 py-1.5 text-xs" data-testid="studio-bg-hex" />
          {bg.type === "gradient" && <>
            <input type="color" value={bg.color2} onChange={(e) => set({ color2: e.target.value })} className="h-9 w-12 rounded border border-[var(--mv-border-2)] bg-transparent" data-testid="studio-bg-color2" />
            <label className="text-[11px]">{tx("Angle")} <input type="range" min={0} max={360} value={bg.angle} onChange={(e) => set({ angle: +e.target.value })} className="accent-[var(--mv-teal)]" data-testid="studio-bg-angle" /></label>
          </>}
        </div>
      )}
      {bg.type === "image" && <button onClick={() => fileRef.current.click()} className="flex items-center gap-1.5 text-xs text-[var(--mv-teal)]" data-testid="studio-bg-image-change"><ImagePlus className="h-3.5 w-3.5" />{tx("Change background image")}</button>}
      {bg.type !== "transparent" && (
        <label className="block text-[11px]">{tx("Background opacity")} {bg.opacity}%
          <input type="range" min={0} max={100} value={bg.opacity} onChange={(e) => set({ opacity: +e.target.value })} className="w-full accent-[var(--mv-teal)]" data-testid="studio-bg-opacity" />
        </label>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onFile} data-testid="studio-bg-file" />
    </div>
  );
}
